import { reportServerError } from './ErrorTrackingService';

const DEFAULT_TIMEOUT_MS = 30_000;
const FILE_TRANSFER_TIMEOUT_MS = 120_000;

export const DATA_CHANGED_EVENT = 'ra:data-changed';

export interface DemoRequestHandler {
  handle(method: string, url: string, body?: unknown): Promise<unknown>;
  download(url: string): Promise<Blob>;
}

export class BackendApiClient {
  private readonly baseUrl: string;
  private authenticated = false;
  private demo: DemoRequestHandler | null = null;

  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_URL ?? '';
  }

  async get<T>(url: string): Promise<T> {
    return this.sendRequest<T>('GET', url);
  }

  async post<T>(url: string, body?: unknown, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<T> {
    return this.sendRequest<T>('POST', url, { body }, timeoutMs);
  }

  async put<T>(url: string, body?: unknown): Promise<T> {
    return this.sendRequest<T>('PUT', url, { body });
  }

  async patch<T>(url: string, body?: unknown): Promise<T> {
    return this.sendRequest<T>('PATCH', url, { body });
  }

  async delete(url: string): Promise<void> {
    await this.sendRequest<void>('DELETE', url);
  }

  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    return this.sendRequest<T>('POST', url, { formData }, FILE_TRANSFER_TIMEOUT_MS);
  }

  async downloadFile(url: string, fileName: string): Promise<void> {
    if (this.demo) {
      this.saveBlob(await this.demo.download(url), fileName);
      return;
    }
    const response = await this.fetchWithTimeout(
      `${this.baseUrl}${url}`,
      { credentials: 'include' },
      FILE_TRANSFER_TIMEOUT_MS
    );
    if (!response.ok) {
      const error = new Error(`Nedladdningen misslyckades (felkod ${response.status}). Försök igen.`);
      reportServerError(error, { url, status: response.status });
      throw error;
    }
    this.saveBlob(await response.blob(), fileName);
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }

  markAuthenticated(): void {
    this.authenticated = true;
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  enterDemoMode(handler: DemoRequestHandler): void {
    this.demo = handler;
  }

  exitDemoMode(): void {
    this.demo = null;
  }

  isDemoMode(): boolean {
    return this.demo !== null;
  }

  async clearSession(): Promise<void> {
    if (this.demo) return;
    this.authenticated = false;
    try {
      await this.fetchWithTimeout(`${this.baseUrl}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // best-effort — the httpOnly cookies expire on their own even if this call fails
    }
  }

  private async sendRequest<T>(
    method: string,
    url: string,
    options?: { body?: unknown; formData?: FormData },
    timeoutMs: number = DEFAULT_TIMEOUT_MS
  ): Promise<T> {
    const result = this.demo
      ? (await this.demo.handle(method, url, options?.formData ?? options?.body)) as T
      : await this.sendNetworkRequest<T>(method, url, options, timeoutMs);
    if (method !== 'GET') window.dispatchEvent(new Event(DATA_CHANGED_EVENT));
    return result;
  }

  private async sendNetworkRequest<T>(
    method: string,
    url: string,
    options: { body?: unknown; formData?: FormData } | undefined,
    timeoutMs: number
  ): Promise<T> {
    const response = await this.fetchWithTimeout(`${this.baseUrl}${url}`, this.buildRequestInit(method, options), timeoutMs);
    if (response.status === 401) {
      const refreshed = await this.tryRefreshAccessToken();
      if (refreshed) {
        const retry = await this.fetchWithTimeout(`${this.baseUrl}${url}`, this.buildRequestInit(method, options), timeoutMs);
        return this.parseResponse<T>(retry, method, url);
      }
    }
    return this.parseResponse<T>(response, method, url);
  }

  private buildRequestInit(method: string, options?: { body?: unknown; formData?: FormData }): RequestInit {
    const base: RequestInit = { method, credentials: 'include' };
    if (options?.formData) {
      return { ...base, body: options.formData };
    }
    if (options === undefined) {
      return base;
    }
    return {
      ...base,
      headers: { 'Content-Type': 'application/json' },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    };
  }

  private async parseResponse<T>(response: Response, method: string, url: string): Promise<T> {
    if (!response.ok) {
      const error = new Error(await this.extractErrorMessage(response, `Något gick fel (felkod ${response.status}). Försök igen.`));
      reportServerError(error, { url: `${method} ${url}`, status: response.status });
      throw error;
    }
    const text = await response.text();
    return text ? JSON.parse(text) : (undefined as T);
  }

  private async tryRefreshAccessToken(): Promise<boolean> {
    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/auth/refresh`, { method: 'POST', credentials: 'include' });
      this.authenticated = response.ok;
      return response.ok;
    } catch {
      this.authenticated = false;
      return false;
    }
  }

  private async fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<Response> {
    if (this.demo) throw new Error('Blockerad i demoläget');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...init, signal: controller.signal });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        const timeout = new Error('Servern svarade inte i tid. Försök igen.');
        reportServerError(timeout, { url });
        throw timeout;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async extractErrorMessage(response: Response, fallback: string): Promise<string> {
    try {
      const text = await response.text();
      if (text) {
        const json = JSON.parse(text);
        if (typeof json.message === 'string') return json.message;
        if (typeof json.title === 'string') return json.title;
      }
    } catch {
      // ignore parse errors
    }
    return fallback;
  }
}

export const backendApiClient = new BackendApiClient();
